import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { SquarePen, CircleDot, BookOpenText, Mail, Settings as SettingsIcon } from 'lucide-react'
import type { ConversationSummary } from './api'
import type { ScreenName } from '../../shared/types/navigation'
import { relativeTime } from '@tendbot/ui'

interface AppSidebarProps {
  conversations: ConversationSummary[]
  activeConversationId: string | null
  currentConversationId: string | null
  collapsed: boolean
  onToggleCollapse: () => void
  onSelectConversation: (id: string) => void
  onNewChat: () => void
  onNavigate?: (screen: ScreenName) => void
  activeTab?: ScreenName
  loading: boolean
}

export const AppSidebar = memo(function AppSidebar({
  conversations,
  currentConversationId,
  collapsed,
  onToggleCollapse,
  onSelectConversation,
  onNewChat,
  onNavigate,
  activeTab = 'chat',
  loading,
}: AppSidebarProps) {
  const { t } = useTranslation('chat')
  const { t: tc } = useTranslation('common')

  const effectiveTab = ['briefing', 'plugins', 'cortex', 'activity'].includes(activeTab) ? 'settings' : activeTab

  const handleNavClick = (screen: ScreenName) => {
    onNavigate?.(screen)
  }

  if (collapsed) {
    return (
      <div className="w-10 flex flex-col items-center py-3 border-r border-gold/10 bg-charcoal shrink-0">
        <button
          onClick={onToggleCollapse}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
          aria-label={t('sidebar.expand')}
        >
          <span className="block h-8 w-8 rounded-full overflow-hidden ring-1 ring-gold/20 bg-gold/10">
            <img
              src="/tendbot.png"
              alt=""
              aria-hidden
              decoding="async"
              className="block h-[175%] w-[175%] max-w-none -translate-x-[21.5%] -translate-y-[5%]"
            />
          </span>
        </button>

        <div className="flex-1" />

        <div className="flex flex-col items-center gap-1">
          {([
            { id: 'mail' as ScreenName, icon: Mail, label: tc('tabs.mail') },
            { id: 'followups' as ScreenName, icon: CircleDot, label: tc('tabs.followups') },
            { id: 'notebook' as ScreenName, icon: BookOpenText, label: tc('tabs.notebook') },
            { id: 'settings' as ScreenName, icon: SettingsIcon, label: tc('tabs.settings') },
          ]).map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => handleNavClick(id)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                effectiveTab === id ? 'text-gold bg-gold/10' : 'text-pearl/50 hover:text-pearl/70 hover:bg-white/5'
              }`}
              title={label}
            >
              <Icon size={16} />
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="w-[280px] flex flex-col border-r border-gold/10 bg-charcoal shrink-0">
      {/* Header — Logo left, New Chat right */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-gold/10">
        <button
          onClick={onToggleCollapse}
          className="flex items-center rounded-lg p-1 hover:bg-white/5 transition-colors"
          title={t('sidebar.collapse')}
        >
          <span className="block h-10 w-10 rounded-full overflow-hidden ring-1 ring-gold/20 bg-gold/10">
            <img
              src="/tendbot.png"
              alt=""
              aria-hidden
              decoding="async"
              className="block h-[175%] w-[175%] max-w-none -translate-x-[21.5%] -translate-y-[5%]"
            />
          </span>
        </button>
        <button
          onClick={onNewChat}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-gold text-sm font-medium hover:bg-gold/10 transition-colors"
        >
          <SquarePen size={16} />
          {t('sidebar.newChat')}
        </button>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="px-3 py-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-14 mb-1 rounded-lg bg-white/[0.03] animate-pulse" />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <p className="px-3 py-4 text-pearl/55 text-xs text-center">
            {t('sidebar.noConversations')}
          </p>
        ) : (
          <div className="py-1">
            {conversations.map((conv, idx) => {
              const isCurrent = conv.id === currentConversationId
              const isActive = conv.isActive
              const showArchiveDivider = !isActive && idx > 0 && conversations[idx - 1]?.isActive

              return (
                <div key={conv.id}>
                  {showArchiveDivider && (
                    <div className="flex items-center gap-2 px-3 pt-3 pb-1">
                      <div className="flex-1 h-px bg-pearl/8" />
                      <span className="text-[10px] uppercase tracking-widest text-pearl/45 font-medium">{t('sidebar.archiveDivider')}</span>
                      <div className="flex-1 h-px bg-pearl/8" />
                    </div>
                  )}
                  <button
                    onClick={() => onSelectConversation(conv.id)}
                    className={`w-full text-left px-3 py-2.5 transition-all duration-200 border-l-2 ${
                      isCurrent && effectiveTab === 'chat' && isActive
                        ? 'bg-gold/10 border-gold text-ivory shadow-[inset_0_0_20px_rgba(200,149,48,0.06)]'
                        : isCurrent && effectiveTab === 'chat'
                          ? 'bg-white/[0.06] border-pearl/30 text-pearl/70'
                          : isCurrent
                            ? 'bg-white/[0.04] border-pearl/20 text-pearl/60'
                            : isActive
                              ? 'border-gold/30 text-ivory/80 hover:bg-gold/5 hover:text-ivory'
                              : 'border-transparent text-pearl/55 hover:bg-white/[0.03] hover:text-pearl/75'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <p className={`text-sm leading-5 line-clamp-2 flex-1 ${!isActive && !isCurrent ? 'font-normal' : 'font-medium'}`}>
                        {conv.title || conv.lastMessage || '\u00A0'}
                      </p>
                      {isActive && (
                        <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-gold shadow-[0_0_6px_rgba(200,149,48,0.5)]" />
                      )}
                    </div>
                    <p className={`text-[11px] mt-0.5 ${isActive ? 'text-gold/50' : 'opacity-30'}`}>
                      {relativeTime(conv.updatedAt)}
                    </p>
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Nav footer — Follow-ups + Notebook + Settings */}
      <div className="border-t border-gold/10">
        {([
          { id: 'mail' as ScreenName, label: tc('tabs.mail'), icon: Mail },
          { id: 'followups' as ScreenName, label: tc('tabs.followups'), icon: CircleDot },
          { id: 'notebook' as ScreenName, label: tc('tabs.notebook'), icon: BookOpenText },
          { id: 'settings' as ScreenName, label: tc('tabs.settings'), icon: SettingsIcon },
        ]).map(({ id, label, icon: Icon }) => {
          const active = effectiveTab === id
          return (
            <button
              key={id}
              onClick={() => handleNavClick(id)}
              className={`w-full flex items-center gap-4 px-6 py-4 text-sm font-medium transition-colors border-l-2 ${
                active
                  ? 'bg-gold/10 border-gold text-ivory'
                  : 'border-transparent text-pearl/50 hover:bg-white/5 hover:text-pearl/70'
              }`}
            >
              <Icon size={18} strokeWidth={active ? 2 : 1.5} />
              {label}
            </button>
          )
        })}
      </div>
    </div>
  )
})
